import { container } from '@databases/service-container';

import {
  CopyClipboardEventCommand,
  CutClipboardEventCommand,
} from '@canvas/databases-bridge-api/commands';
import { type RovoContentBridgeAPI, type client } from '@canvas/rovo-content-bridge-api';

type ForwardClipboardEventsParams = {
  bridge: RovoContentBridgeAPI;
  localClientId: ReturnType<typeof client>;
  externalClientId: ReturnType<typeof client>;
};

/**
 * Sets up clipboard event forwarding from the database iframe to the parent via the bridge.
 * This forwards copy/cut events when databases has decided not to handle them (empty payload case).
 *
 * Uses the event bus to listen for clipboard forwarding events from databases copy/paste hooks.
 */
export function setupClipboardEventForwarding({
  bridge,
  localClientId,
  externalClientId,
}: ForwardClipboardEventsParams): () => void {
  const eventBus = container.resolve('EventBus');

  const forwardCopyEvent = () => {
    bridge.send(CopyClipboardEventCommand, {
      senderId: localClientId,
      receiverId: externalClientId,
    });
  };

  const forwardCutEvent = () => {
    bridge.send(CutClipboardEventCommand, {
      senderId: localClientId,
      receiverId: externalClientId,
    });
  };

  eventBus.on('forwardCopyClipboardToParent', forwardCopyEvent);
  eventBus.on('forwardCutClipboardToParent', forwardCutEvent);

  return () => {
    eventBus.remove('forwardCopyClipboardToParent', forwardCopyEvent);
    eventBus.remove('forwardCutClipboardToParent', forwardCutEvent);
  };
}
